import React, { useEffect, useState } from 'react';
import { GetResourceUtilizationResponse } from '../../api-service/clients';
import { DatePickerValue } from '../../components/date-picker';
import { offerAndActivityAreaPathName, viewActivityPathName } from '../offer-and-activity/offer-and-activity-area-pathnames';
import { today, toDate } from '../../utilities';
import { getResourceUtilization } from './resource-utilization-api';
import {
  buildResourceUtilizationOverview,
  drillDown,
  filterChips,
  offersForUnit,
  removeFilter,
  ResourceUtilizationFilters,
  ResourceUtilizationView,
  selectOffer,
  selectServiceProviderUnit,
} from './resource-utilization';
import { ResourceUtilizationComponent } from './resource-utilization-component';

const emptyData: GetResourceUtilizationResponse = { serviceProviderUnits: [], offers: [], grantTypes: [], employees: [], capacities: [], allocations: [] };

export function ResourceUtilization() {
  const [date, setDate] = useState<Date>(today());
  const [data, setData] = useState<GetResourceUtilizationResponse>(emptyData);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<ResourceUtilizationFilters>({});
  const [view, setView] = useState<ResourceUtilizationView>('employee');

  useEffect(() => {
    let isCurrent = true;
    setIsLoading(true);
    getResourceUtilization(date)
      .then(response => isCurrent && setData(response))
      .finally(() => isCurrent && setIsLoading(false));
    return () => {
      isCurrent = false;
    };
  }, [date]);

  function onChangeDate(value: DatePickerValue) {
    const selected = toDate(value);
    if (selected) {
      setDate(selected);
    }
  }

  function onSelectServiceProviderUnit(serviceProviderUnitId: string | undefined) {
    setFilters(selectServiceProviderUnit(data, filters, serviceProviderUnitId));
  }

  function onSelectOffer(offerId: string | undefined) {
    setFilters(selectOffer(data, filters, offerId));
  }

  function onSelectGrantType(grantTypeId: string | undefined) {
    setFilters({ ...filters, grantTypeId });
  }

  function onRemoveFilter(filter: keyof ResourceUtilizationFilters) {
    setFilters(removeFilter(filters, filter));
  }

  function onResetFilters() {
    setFilters({});
  }

  function onDrillDown(key: string) {
    const next = drillDown(data, filters, view, key);
    setFilters(next.filters);
    setView(next.view);
  }

  function onOpenActivity(activityId: string) {
    window.open(`/${offerAndActivityAreaPathName}/${viewActivityPathName}/${activityId}`);
  }

  return (
    <ResourceUtilizationComponent
      date={date}
      isLoading={isLoading}
      serviceProviderUnits={data.serviceProviderUnits}
      offers={offersForUnit(data, filters.serviceProviderUnitId)}
      grantTypes={data.grantTypes}
      filters={filters}
      chips={filterChips(data, filters)}
      view={view}
      overview={buildResourceUtilizationOverview(data, filters, view)}
      onChangeDate={onChangeDate}
      onSelectServiceProviderUnit={onSelectServiceProviderUnit}
      onSelectOffer={onSelectOffer}
      onSelectGrantType={onSelectGrantType}
      onRemoveFilter={onRemoveFilter}
      onResetFilters={onResetFilters}
      onSelectView={setView}
      onDrillDown={onDrillDown}
      onOpenActivity={onOpenActivity}
    />
  );
}
